import { forwardRef } from "react";
import type { TextareaHTMLAttributes, ReactNode } from "react";
import { cn } from "@/utils";
import { FieldShell } from "./FieldShell";

export interface TextAreaFieldProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "onChange"> {
  label?: ReactNode;
  error?: string | null;
  hint?: ReactNode;
  /** Show a live `count/maxLength` indicator beside the label. */
  showCount?: boolean;
  /**
   * Show a live `count/countLimit` indicator without capping input (no native
   * `maxLength`), so pasted or prefilled text over the limit stays visible and
   * the counter turns red instead of the text being silently cut.
   */
  countLimit?: number;
  containerClassName?: string;
  onChange?: (value: string) => void;
}

/** Glass textarea with the electric focus glow and an optional char counter. */
export const TextAreaField = forwardRef<HTMLTextAreaElement, TextAreaFieldProps>(
  (
    {
      label,
      error,
      hint,
      required,
      id,
      className,
      containerClassName,
      maxLength,
      showCount,
      countLimit,
      value,
      onChange,
      ...props
    },
    ref,
  ) => {
    // With countLimit the count matches the validator, which checks the trimmed
    // text; a native maxLength counts every character typed.
    const count =
      typeof value === "string" ? (countLimit !== undefined ? value.trim().length : value.length) : 0;
    const limit = countLimit ?? (showCount ? maxLength : undefined);
    const over = countLimit !== undefined && count > countLimit;
    return (
      <FieldShell
        id={id}
        label={label}
        required={required}
        error={error}
        hint={hint}
        className={containerClassName}
        action={
          limit ? (
            <span
              className={cn("text-[11px] tabular-nums", over && "font-semibold text-destructive")}
              style={over ? undefined : { color: "var(--landing-text-dim)" }}
            >
              {count}/{limit}
            </span>
          ) : undefined
        }
      >
        <textarea
          ref={ref}
          id={id}
          required={required}
          aria-invalid={error ? true : undefined}
          value={value}
          maxLength={maxLength}
          onChange={(e) => onChange?.(e.target.value)}
          className={cn(
            "auth-input block w-full resize-y rounded-xl px-3.5 py-3 text-sm leading-relaxed outline-none placeholder:text-[color:var(--landing-text-dim)]",
            error && "!border-destructive/60",
            className,
          )}
          style={{ color: "var(--landing-text)", minHeight: "120px" }}
          {...props}
        />
      </FieldShell>
    );
  },
);
TextAreaField.displayName = "TextAreaField";
